import SearchableDropdown from '../../../common/SearchableDropdown';
import { BaseControl } from '.';
import { db } from '../../../../firebase/config';
import { client } from '../../../../hooks/Client';

// AI-CONTEXT: See docs/features/livereport-widgets-housevisits-personas.md
// FirestoreSelectControl — generic single-select dropdown whose options
// are loaded from a Firestore collection at widget mount. Used by widgets
// in the Housevisits/Personas feature for things like Locations,
// PersonaRoutes, individual Personas, etc.
//
// Construction parameters drive the query:
//   collection:    Firestore collection name
//   valueField:    doc field to use as the option's `value` (default 'ID')
//   labelField:    doc field to render as the visible label (default 'Title')
//   orderBy:       optional doc field to .orderBy('asc') on (default 'Position')
//   includeAll:    if true, prepend a sentinel '' option labelled 'Alles'
//   filterByCompany: if true (default), .where('CompagnyID', '==', client)
//
// Options are loaded via the existing BaseControl async-options mechanism —
// the control's `load()` runs once per editor session.

/**
 * @typedef {object} FirestoreSelectControlConfig
 * @property {string} collection
 * @property {string} [valueField='ID']
 * @property {string} [labelField='Title']
 * @property {string|null} [orderBy='Position']
 * @property {boolean} [includeAll=false]
 * @property {string} [allLabel='Alles']
 * @property {boolean} [filterByCompany=true]
 */

/**
 * @extends {BaseControl<string>}
 */
export class FirestoreSelectControl extends BaseControl {

    /**
     * @param {{
     *   label: string,
     *   description?: string,
     *   defaultValue?: string,
     *   firestore: FirestoreSelectControlConfig,
     * }} args
     */
    constructor({ label, description, defaultValue = '', firestore }) {
        const cfg = {
            valueField: 'ID',
            labelField: 'Title',
            orderBy: 'Position',
            includeAll: false,
            allLabel: 'Alles',
            filterByCompany: true,
            ...firestore,
        };
        super({
            label,
            description,
            defaultValue,
            options: async () => {
                let q = db.collection(cfg.collection);
                if (cfg.filterByCompany) q = q.where('CompagnyID', '==', client || '');
                if (cfg.orderBy) q = q.orderBy(cfg.orderBy, 'asc');
                const snap = await q.get();
                const choices = snap.docs.map(d => {
                    const data = d.data();
                    return {
                        value: String(data[cfg.valueField] ?? d.id),
                        label: String(data[cfg.labelField] ?? data[cfg.valueField] ?? d.id),
                    };
                });
                if (cfg.includeAll) {
                    choices.unshift({ value: '', label: cfg.allLabel });
                }
                return { choices, allowSearch: true };
            },
        });
        this.firestoreConfig = cfg;
    }

    /** @type {BaseControl<string>['render']} */
    render(value, onChange) {
        const choices = this.options?.choices || [];
        return (
            <div className="select-control firestore-select-control">
                <span className="select-control-label">{this.label}</span>
                <SearchableDropdown
                    options={choices}
                    value={value || ''}
                    onChange={onChange}
                    allowSearch
                />
                {this.description && (
                    <p className="select-control-description">{this.description}</p>
                )}
            </div>
        );
    }
}
